import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1OrgBlocksId,
  deleteAdminV1StoresStoreIdBlocksId,
  deleteAdminV1StoresStoreIdSectionsId,
  getAdminV1OrgBlocks,
  getAdminV1OrgBlocksId,
  getAdminV1StoresStoreIdBlocks,
  getAdminV1StoresStoreIdBlocksId,
  getAdminV1StoresStoreIdSections,
  getAdminV1StoresStoreIdSectionsId,
  getAdminV1StoresStoreIdSectionsIdPreview,
  patchAdminV1OrgBlocksId,
  patchAdminV1StoresStoreIdBlocksId,
  patchAdminV1StoresStoreIdSectionsId,
  postAdminV1OrgBlocks,
  postAdminV1OrgBlocksPreview,
  postAdminV1StoresStoreIdBlocks,
  postAdminV1StoresStoreIdBlocksPreview,
  postAdminV1StoresStoreIdSections,
  putAdminV1StoresStoreIdSectionsIdBlocks,
  type ContentBlock,
  type ContentSection,
  type GetAdminV1OrgBlocks200,
  type GetAdminV1StoresStoreIdBlocks200,
  type GetAdminV1StoresStoreIdSections200,
  type GetAdminV1StoresStoreIdSectionsIdPreviewParams,
  type PatchAdminV1StoresStoreIdBlocksIdBody,
  type PatchAdminV1StoresStoreIdSectionsIdBody,
  type PostAdminV1OrgBlocksPreviewParams,
  type PostAdminV1StoresStoreIdBlocksBody,
  type PostAdminV1StoresStoreIdBlocksPreviewBody,
  type PostAdminV1StoresStoreIdSectionsBody,
  type StorefrontContentBlock,
  type StorefrontContentSection,
} from '@nucleus-commerce/api-client/admin';

/**
 * Where a preview resolves: always one store (`storeId`), plus the market by `country` and
 * whether rails embed products. For a store block `storeId` is the block's own store.
 */
export type BlockPreviewParams = PostAdminV1OrgBlocksPreviewParams;

/**
 * Block list params: `q`, `sort`, `kind=banner,rail`, `status=active`, `hasWarnings=true`,
 * `cursor`, `limit`, and on a store's list `scope` (store, shared or all).
 */
export type BlockListParams = ListParams;

/**
 * Blocks of one store, or the organisation's shared blocks when `storeId` is null. The two
 * surfaces take the same bodies, so callers pass the scope and never pick a function.
 */
export const blocksApi = {
  list: (
    storeId: string | null,
    params: BlockListParams,
  ): Promise<GetAdminV1StoresStoreIdBlocks200 | GetAdminV1OrgBlocks200> =>
    storeId === null
      ? getAdminV1OrgBlocks(toSharedParams(params))
      : getAdminV1StoresStoreIdBlocks({ storeId }, params),
  get: (storeId: string | null, id: string): Promise<ContentBlock> =>
    storeId === null
      ? getAdminV1OrgBlocksId({ id })
      : getAdminV1StoresStoreIdBlocksId({ storeId, id }),
  create: (
    storeId: string | null,
    body: PostAdminV1StoresStoreIdBlocksBody,
  ): Promise<ContentBlock> =>
    storeId === null
      ? postAdminV1OrgBlocks(body)
      : postAdminV1StoresStoreIdBlocks({ storeId }, body),
  update: (
    storeId: string | null,
    id: string,
    body: PatchAdminV1StoresStoreIdBlocksIdBody,
  ): Promise<ContentBlock> =>
    storeId === null
      ? patchAdminV1OrgBlocksId({ id }, body)
      : patchAdminV1StoresStoreIdBlocksId({ storeId, id }, body),
  remove: (storeId: string | null, id: string): Promise<void> =>
    storeId === null
      ? deleteAdminV1OrgBlocksId({ id })
      : deleteAdminV1StoresStoreIdBlocksId({ storeId, id }),
  /**
   * Unsaved content run through the storefront resolver. A shared block (`storeId` null) goes
   * through the org route and resolves in `params.storeId`; a store block ignores that field.
   */
  preview: (
    storeId: string | null,
    body: PostAdminV1StoresStoreIdBlocksPreviewBody,
    params: BlockPreviewParams,
  ): Promise<StorefrontContentBlock> => {
    if (storeId === null) return postAdminV1OrgBlocksPreview(body, params);
    const { storeId: _previewStoreId, ...rest } = params;
    return postAdminV1StoresStoreIdBlocksPreview({ storeId }, body, rest);
  },
};

function toSharedParams(params: BlockListParams): BlockListParams {
  const { scope: _scope, ...rest } = params;
  return rest;
}

/** Section list params: `q`, `sort`, `status=draft`, `updatedAt_gte=2026-09-01`, `cursor`, `limit`. */
export type SectionListParams = ListParams;

export const sectionsApi = {
  list: (storeId: string, params: SectionListParams): Promise<GetAdminV1StoresStoreIdSections200> =>
    getAdminV1StoresStoreIdSections({ storeId }, params),
  get: (storeId: string, id: string): Promise<ContentSection> =>
    getAdminV1StoresStoreIdSectionsId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdSectionsBody): Promise<ContentSection> =>
    postAdminV1StoresStoreIdSections({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdSectionsIdBody,
  ): Promise<ContentSection> => patchAdminV1StoresStoreIdSectionsId({ storeId, id }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdSectionsId({ storeId, id }),
  setBlocks: (storeId: string, id: string, blockIds: string[]): Promise<ContentSection> =>
    putAdminV1StoresStoreIdSectionsIdBlocks({ storeId, id }, { blockIds }),
  preview: (
    storeId: string,
    id: string,
    params: GetAdminV1StoresStoreIdSectionsIdPreviewParams,
  ): Promise<StorefrontContentSection> =>
    getAdminV1StoresStoreIdSectionsIdPreview({ storeId, id }, params),
};
